import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { MobXProviderContext } from "mobx-react";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { TabPanel, TabView } from "primereact/tabview";
import { Toast } from "primereact/toast";
import { useNavigate } from "react-router-dom";
import { getErrorTitle } from "../../../constants/constants";
import { useResponsive } from "../../../responsive/ResponsiveProvider";
import {
  ROLE_TYPE_CAREGIVER,
  ROLE_TYPE_MHP,
  ROLE_TYPE_NURSE,
  ROLE_TYPE_PROVIDER,
} from "../../../constants/RegisterConstants";
import { RouterAddress } from "../../../constants/RouterAddress";
import Unauthorized from "../../../shared/pages/Unauthorized";
import {
  appAccentPanelStyle,
  appDataTableWrapperStyle,
  appFormSectionStyle,
  appMutedTextStyle,
} from "../../../components/general/uiSurfaceStyles";
import { dashboardPageContainerStyle, renderDashboardHeader } from "../../dashboard/pages/dashboardStyles";
import { useAuthExperience } from "../../auth/hooks/useAuthExperience";

type ResidentOption = {
  residentId: string;
  residentName: string;
};

type ShareTargetOption = {
  actorLinkId?: string;
  counterpartId: string;
  counterpartRoleType: string;
  displayName: string;
  displayEmail: string;
};

const SUPPORTED_CONNECTION_ROLE_OPTIONS = [
  { label: "Facility Admin", value: ROLE_TYPE_PROVIDER },
  { label: "Caregiver", value: ROLE_TYPE_CAREGIVER },
  { label: "Registered Nurse", value: ROLE_TYPE_NURSE },
  { label: "Mental Health Professional", value: ROLE_TYPE_MHP },
];

const ROLE_LABELS: Record<string, string> = {
  [ROLE_TYPE_PROVIDER]: "Facility Admin",
  [ROLE_TYPE_NURSE]: "Registered Nurse",
  [ROLE_TYPE_CAREGIVER]: "Caregiver",
  [ROLE_TYPE_MHP]: "Mental Health Professional",
};

const sectionCardStyle: React.CSSProperties = {
  background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
  border: "1px solid #e2e8f0",
  borderRadius: "18px",
  padding: "1rem",
  boxShadow: "0 14px 32px rgba(15, 23, 42, 0.06)",
};

const mobileSectionShellStyle: React.CSSProperties = {
  border: "1px solid #e2e8f0",
  borderRadius: "16px",
  padding: "1rem",
  background: "#ffffff",
  boxShadow: "0 10px 24px rgba(15, 23, 42, 0.05)",
};

const notificationsScrollerStyle: React.CSSProperties = {
  maxHeight: "34rem",
  overflowY: "auto",
  paddingRight: "0.35rem",
};

const countPillStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: "1.9rem",
  height: "1.9rem",
  padding: "0 0.6rem",
  borderRadius: "999px",
  background: "#fff7ed",
  border: "1px solid #fdba74",
  color: "#9a3412",
  fontWeight: 700,
  fontSize: "0.82rem",
};

const notificationDotStyle: React.CSSProperties = {
  display: "inline-block",
  width: "0.6rem",
  height: "0.6rem",
  borderRadius: "999px",
  background: "#dc2626",
  boxShadow: "0 0 0 3px rgba(220, 38, 38, 0.12)",
};

const isLikelyFullEmailAddress = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());

const getCanonicalRelationshipType = (firstRole: string, secondRole: string) =>
  [String(firstRole || "").trim().toLowerCase(), String(secondRole || "").trim().toLowerCase()]
    .filter(Boolean)
    .sort()
    .join("_");

const getRoleSearchOptions = (currentRoleType: string) => {
  const normalizedRole = String(currentRoleType || "").trim().toLowerCase();
  const filteredOptions = SUPPORTED_CONNECTION_ROLE_OPTIONS.filter((option) => option.value !== normalizedRole);
  return filteredOptions.length ? filteredOptions : SUPPORTED_CONNECTION_ROLE_OPTIONS;
};

const getStatusTone = (status: string) => {
  const normalizedStatus = String(status || "").toLowerCase();

  if (normalizedStatus === "approved" || normalizedStatus === "read") {
    return { background: "#dcfce7", color: "#166534", border: "#86efac" };
  }

  if (normalizedStatus === "pending" || normalizedStatus === "unread") {
    return { background: "#fff7ed", color: "#9a3412", border: "#fdba74" };
  }

  if (normalizedStatus === "rejected" || normalizedStatus === "deleted") {
    return { background: "#fee2e2", color: "#991b1b", border: "#fca5a5" };
  }

  return { background: "#e2e8f0", color: "#334155", border: "#cbd5e1" };
};

const StatusChip = ({ label }: { label: string }) => {
  const tone = getStatusTone(label);

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "999px",
        padding: "0.3rem 0.7rem",
        fontSize: "0.78rem",
        fontWeight: 700,
        textTransform: "capitalize",
        background: tone.background,
        color: tone.color,
        border: `1px solid ${tone.border}`,
      }}
    >
      {String(label || "unknown").replaceAll("_", " ")}
    </span>
  );
};

const TabHeader = ({ label, count, showDot }: { label: string; count?: number; showDot?: boolean }) => (
  <div
    className="flex align-items-center justify-content-between gap-2"
    style={{ minWidth: "13.5rem", width: "100%" }}
  >
    <span>{label}</span>
    <span className="flex align-items-center gap-2" style={{ minWidth: "4.1rem", justifyContent: "flex-end" }}>
      <span style={{ ...countPillStyle, visibility: typeof count === "number" ? "visible" : "hidden" }}>
        {typeof count === "number" ? count : 0}
      </span>
      <span style={{ ...notificationDotStyle, visibility: showDot ? "visible" : "hidden" }} />
    </span>
  </div>
);

const RelationshipCenterPage = () => {
  const toast = useRef<Toast>(null);
  const navigate = useNavigate();
  const mobile = useResponsive().isMobile;
  const { backendAPIStore, authStore } = useContext(MobXProviderContext);
  const authExperience = useAuthExperience();
  const currentActorId = authStore.getUserId();
  const currentRoleType = authStore.getUserRoleType();

  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [actorLinks, setActorLinks] = useState<any[]>([]);
  const [residentRoleLinks, setResidentRoleLinks] = useState<any[]>([]);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [providerResidentOptions, setProviderResidentOptions] = useState<ResidentOption[]>([]);
  const [selectedResidentId, setSelectedResidentId] = useState("");
  const [selectedApprovedActorLinkId, setSelectedApprovedActorLinkId] = useState("");
  const [selectedApprovedActorId, setSelectedApprovedActorId] = useState("");
  const [selectedShareTarget, setSelectedShareTarget] = useState<ShareTargetOption | null>(null);
  const [residentRequestError, setResidentRequestError] = useState<string | null>(null);

  const searchRoleOptions = useMemo(
    () => getRoleSearchOptions(currentRoleType),
    [currentRoleType],
  );
  const [searchRoleType, setSearchRoleType] = useState(searchRoleOptions[0]?.value || ROLE_TYPE_NURSE);

  const canAccessPage =
    currentRoleType === ROLE_TYPE_PROVIDER
    || currentRoleType === ROLE_TYPE_NURSE
    || currentRoleType === ROLE_TYPE_CAREGIVER
    || currentRoleType === ROLE_TYPE_MHP;

  const refreshRelationshipData = async () => {
    setLoading(true);
    try {
      await backendAPIStore.init();
      const [actorLinkResults, residentRoleLinkResults, notificationResults] = await Promise.all([
        backendAPIStore.getActorLinks(),
        backendAPIStore.getResidentRoleLinks(),
        backendAPIStore.getRelationshipNotifications(),
      ]);
      setActorLinks(actorLinkResults || []);
      setResidentRoleLinks(residentRoleLinkResults || []);
      setNotifications(notificationResults || []);
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error.message, life: 3000 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSearchRoleType(searchRoleOptions[0]?.value || ROLE_TYPE_NURSE);
  }, [searchRoleOptions]);

  useEffect(() => {
    if (!canAccessPage) {
      return;
    }

    void refreshRelationshipData();
  }, [canAccessPage]);

  useEffect(() => {
    if (!canAccessPage || currentRoleType !== ROLE_TYPE_PROVIDER) {
      setProviderResidentOptions([]);
      return;
    }

    const loadProviderResidents = async () => {
      try {
        await backendAPIStore.init();
        const response = await backendAPIStore.getHomeListForProvider();
        const residentEntries = await Promise.all(
          (response?.homes || []).map(async (home: any) => {
            if (!home?.homeId) {
              return [] as ResidentOption[];
            }

            const residents = await backendAPIStore.getResidentsForHome(home.homeId);
            return (residents || [])
              .filter((resident: any) => resident?.residentId)
              .map((resident: any) => ({
                residentId: resident.residentId,
                residentName: `${resident.firstName || ""} ${resident.lastName || ""}`.trim() || "Resident",
              }));
          }),
        );

        setProviderResidentOptions(residentEntries.flat());
      } catch {
        setProviderResidentOptions([]);
      }
    };

    void loadProviderResidents();
  }, [backendAPIStore, canAccessPage, currentRoleType]);

  const approvedActorLinks = useMemo(
    () => actorLinks.filter((link: any) => String(link.status || "").toLowerCase() === "approved"),
    [actorLinks],
  );

  const visibleActorLinks = useMemo(
    () =>
      actorLinks.filter((link: any) => {
        const status = String(link.status || "").toLowerCase();
        return status === "approved" || status === "pending";
      }),
    [actorLinks],
  );

  const visibleResidentRoleLinks = useMemo(
    () =>
      residentRoleLinks.filter((link: any) => {
        const status = String(link.status || "").toLowerCase();
        return status === "approved" || status === "pending";
      }),
    [residentRoleLinks],
  );

  const pendingIncomingActorLinksCount = useMemo(
    () =>
      actorLinks.filter(
        (link: any) => String(link.status || "").toLowerCase() === "pending" && link.direction === "incoming",
      ).length,
    [actorLinks],
  );

  const pendingIncomingResidentRoleLinksCount = useMemo(
    () =>
      residentRoleLinks.filter(
        (link: any) => String(link.status || "").toLowerCase() === "pending" && link.direction === "incoming",
      ).length,
    [residentRoleLinks],
  );

  const approvedActorLinkOptions = useMemo(() => {
    return approvedActorLinks.map((link: any) => {
      const counterpartId = link.requesterActorId === currentActorId ? link.targetActorId : link.requesterActorId;
      const counterpartRoleType = String(
        link.requesterActorId === currentActorId ? link.targetRoleType : link.requesterRoleType,
      ).toLowerCase();
      const displayName = link.requesterActorId === currentActorId
        ? (link.metadata?.targetDisplayName || counterpartId)
        : (link.metadata?.requesterDisplayName || counterpartId);
      const displayEmail = link.requesterActorId === currentActorId
        ? (link.metadata?.targetEmail || "")
        : (link.metadata?.requesterEmail || "");

      return {
        label: `${displayName}${displayEmail ? ` - ${displayEmail}` : ""}`,
        value: link.linkId,
        counterpartId,
        counterpartRoleType,
        displayName,
        displayEmail,
      };
    });
  }, [approvedActorLinks, currentActorId]);

  useEffect(() => {
    if (approvedActorLinkOptions.length === 0) {
      setSelectedApprovedActorLinkId("");
      setSelectedApprovedActorId("");
      return;
    }

    const matchedOption = approvedActorLinkOptions.find((option) => option.value === selectedApprovedActorLinkId);
    if (matchedOption) {
      setSelectedApprovedActorId(matchedOption.counterpartId);
      return;
    }

    setSelectedApprovedActorLinkId(approvedActorLinkOptions[0].value);
    setSelectedApprovedActorId(approvedActorLinkOptions[0].counterpartId);
  }, [approvedActorLinkOptions, selectedApprovedActorLinkId]);

  const onSearch = async () => {
    const normalizedEmail = searchQuery.trim().toLowerCase();

    if (!normalizedEmail || !isLikelyFullEmailAddress(normalizedEmail)) {
      setSearchResults([]);
      setSearchError("Enter the full email address to search safely.");
      return;
    }

    setSearchError(null);
    setSearching(true);
    try {
      await backendAPIStore.init();
      const response = await backendAPIStore.searchActors({
        email: normalizedEmail,
        roleType: searchRoleType,
      });
      const filtered = (response || []).filter((item: any) => item.userId !== currentActorId);
      setSearchResults(filtered || []);
      if (filtered.length === 0) {
        setSearchError(`No ${ROLE_LABELS[searchRoleType]?.toLowerCase() || searchRoleType} user matched that email address.`);
      }
    } catch (error: any) {
      setSearchError(error.message || "Unable to search users right now.");
    } finally {
      setSearching(false);
    }
  };

  const onCreateActorLink = async (user: any) => {
    try {
      await backendAPIStore.createActorLink({
        targetActorId: user.userId,
        targetRoleType: user.userRoleType,
        relationshipType: getCanonicalRelationshipType(currentRoleType, user.userRoleType),
        metadata: {
          requesterDisplayName: authStore.getUserName() || authStore.getUserEmailId() || "Current user",
          requesterEmail: authStore.getUserEmailId() || "",
          targetDisplayName: `${user.firstName || ""} ${user.lastName || ""}`.trim(),
          targetEmail: user.email || "",
          requesterRoleType: currentRoleType,
        },
      });
      toast.current?.show({ severity: "success", summary: "Request sent", detail: "Connection request sent successfully.", life: 2500 });
      setSearchResults([]);
      setSearchQuery("");
      await refreshRelationshipData();
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error.message, life: 3000 });
    }
  };

  const onPrepareResidentShare = (target: ShareTargetOption) => {
    setSelectedShareTarget(target);
    setSelectedApprovedActorLinkId(target.actorLinkId || "");
    setSelectedApprovedActorId(target.counterpartId);
    setResidentRequestError(null);
  };

  const onUpdateActorLink = async (link: any, status: string) => {
    try {
      await backendAPIStore.updateActorLinkStatus({ linkId: link.linkId, status });
      toast.current?.show({ severity: "success", summary: "Updated", detail: `Connection request ${status}.`, life: 2500 });
      await refreshRelationshipData();
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error.message, life: 3000 });
    }
  };

  const onCreateResidentRoleLink = async () => {
    if (!selectedResidentId) {
      setResidentRequestError("Choose a resident to share.");
      return;
    }

    const selectedResident = providerResidentOptions.find((resident) => resident.residentId === selectedResidentId);
    const selectedActorLink = approvedActorLinkOptions.find((option) => option.value === selectedApprovedActorLinkId);
    const resolvedTarget = selectedShareTarget || (selectedActorLink ? {
      actorLinkId: selectedActorLink.value,
      counterpartId: selectedActorLink.counterpartId,
      counterpartRoleType: selectedActorLink.counterpartRoleType,
      displayName: selectedActorLink.displayName,
      displayEmail: selectedActorLink.displayEmail,
    } : null);

    if (!selectedResident || !resolvedTarget) {
      setResidentRequestError("Choose who should receive this resident share.");
      return;
    }

    setResidentRequestError(null);
    try {
      await backendAPIStore.createResidentRoleLink({
        actorLinkId: resolvedTarget.actorLinkId || "",
        providerResidentId: selectedResident.residentId,
        linkedActorId: resolvedTarget.counterpartId,
        roleType: resolvedTarget.counterpartRoleType,
        metadata: {
          providerResidentName: selectedResident.residentName,
          linkedActorDisplayName: resolvedTarget.displayName,
          linkedActorEmail: resolvedTarget.displayEmail,
        },
      });
      toast.current?.show({ severity: "success", summary: "Request sent", detail: "Resident access request sent successfully.", life: 2500 });
      setSelectedShareTarget(null);
      setSelectedApprovedActorLinkId("");
      setSelectedApprovedActorId("");
      await refreshRelationshipData();
    } catch (error: any) {
      setResidentRequestError(error.message || "Unable to send resident access request.");
    }
  };

  const onUpdateResidentRoleLink = async (link: any, status: string) => {
    try {
      await backendAPIStore.updateResidentRoleLinkStatus({ linkId: link.linkId, status });
      toast.current?.show({ severity: "success", summary: "Updated", detail: `Resident access request ${status}.`, life: 2500 });
      await refreshRelationshipData();
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error.message, life: 3000 });
    }
  };

  const onMarkNotificationRead = async (notificationId: string) => {
    try {
      await backendAPIStore.updateRelationshipNotification({ notificationId, markRead: true });
      await refreshRelationshipData();
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error.message, life: 3000 });
    }
  };

  if (!canAccessPage) {
    return <Unauthorized />;
  }

  const renderMobileUserCard = (title: string, subtitle?: string, actions?: React.ReactNode, extra?: React.ReactNode) => (
    <div
      style={{
        border: "1px solid #e2e8f0",
        borderRadius: "16px",
        padding: "0.95rem",
        background: "#ffffff",
        display: "flex",
        flexDirection: "column",
        gap: "0.75rem",
      }}
    >
      <div>
        <div style={{ fontWeight: 700, color: "#0f172a" }}>{title}</div>
        {subtitle ? <div style={{ ...appMutedTextStyle, fontSize: "0.88rem", marginTop: "0.2rem" }}>{subtitle}</div> : null}
      </div>
      {extra}
      {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
    </div>
  );

  const findUsersSection = (
    <>
      <div style={appAccentPanelStyle} className="mb-3">
        <div style={{ fontWeight: 700, color: "#9a3412" }}>Find people and decide the next step</div>
        <div className="mt-2" style={appMutedTextStyle}>
          Search {authExperience.brandName} users by name or email. You can either create a general connection or, if you are a facility admin, jump straight into sharing a resident.
        </div>
      </div>
      <div style={appFormSectionStyle}>
        <div className="grid">
          <div className="col-12 md:col-4">
            <label className="mb-2 block">Role</label>
            <Dropdown
              options={searchRoleOptions}
              optionLabel="label"
              optionValue="value"
              value={searchRoleType}
              onChange={(e) => setSearchRoleType(e.value)}
              style={{ width: "100%" }}
            />
          </div>
          <div className="col-12 md:col-8">
            <label className="mb-2 block">Search</label>
            <div className="flex gap-2 flex-wrap">
              <InputText
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name or email"
                style={{ width: "100%", flex: "1 1 14rem" }}
              />
              <Button type="button" label="Search" severity="warning" rounded loading={searching} onClick={onSearch} />
            </div>
          </div>
        </div>
        {searchError ? <div className="p-error mt-3">{searchError}</div> : null}
      </div>

      <div className="mt-4" style={{ ...appDataTableWrapperStyle, borderRadius: "16px", overflow: "hidden" }}>
        {mobile ? (
          <div className="flex flex-column gap-3">
            {searchResults.length === 0 ? (
              <div style={{ ...appMutedTextStyle, padding: "0.25rem 0.1rem" }}>No matching {authExperience.brandName} users found yet.</div>
            ) : searchResults.map((rowData: any) => renderMobileUserCard(
              `${`${rowData.firstName || ""} ${rowData.lastName || ""}`.trim() || rowData.email}`,
              `${ROLE_LABELS[String(rowData.userRoleType || "").toLowerCase()] || rowData.userRoleType}${rowData.email ? ` - ${rowData.email}` : ""}`,
              <>
                <Button type="button" label="Connect" severity="warning" rounded size="small" onClick={() => onCreateActorLink(rowData)} />
                {currentRoleType === ROLE_TYPE_PROVIDER ? (
                  <Button
                    type="button"
                    label="Share Resident"
                    severity="secondary"
                    outlined
                    rounded
                    size="small"
                    onClick={() => onPrepareResidentShare({
                      counterpartId: rowData.userId,
                      counterpartRoleType: String(rowData.userRoleType || "").toLowerCase(),
                      displayName: `${rowData.firstName || ""} ${rowData.lastName || ""}`.trim() || rowData.email || "AFH Hub user",
                      displayEmail: rowData.email || "",
                    })}
                  />
                ) : null}
              </>,
            ))}
          </div>
        ) : (
          <DataTable value={searchResults} size="small" emptyMessage={`No matching ${authExperience.brandName} users found yet.`}>
            <Column
              header={`${authExperience.brandName} User`}
              body={(rowData: any) => (
                <div>
                  <div style={{ fontWeight: 700, color: "#0f172a" }}>
                    {`${rowData.firstName || ""} ${rowData.lastName || ""}`.trim() || rowData.email}
                  </div>
                  <div style={{ ...appMutedTextStyle, fontSize: "0.88rem" }}>
                    {ROLE_LABELS[String(rowData.userRoleType || "").toLowerCase()] || rowData.userRoleType}
                    {rowData.email ? ` - ${rowData.email}` : ""}
                  </div>
                </div>
              )}
            />
            <Column
              header=""
              body={(rowData: any) => (
                <div className="flex flex-wrap gap-2 justify-content-end">
                  <Button type="button" label="Connect" severity="warning" rounded size="small" onClick={() => onCreateActorLink(rowData)} />
                  {currentRoleType === ROLE_TYPE_PROVIDER ? (
                    <Button
                      type="button"
                      label="Share Resident"
                      severity="secondary"
                      outlined
                      rounded
                      size="small"
                      onClick={() => onPrepareResidentShare({
                        counterpartId: rowData.userId,
                        counterpartRoleType: String(rowData.userRoleType || "").toLowerCase(),
                        displayName: `${rowData.firstName || ""} ${rowData.lastName || ""}`.trim() || rowData.email || "AFH Hub user",
                        displayEmail: rowData.email || "",
                      })}
                    />
                  ) : null}
                </div>
              )}
              style={{ width: currentRoleType === ROLE_TYPE_PROVIDER ? "17rem" : "9rem" }}
            />
          </DataTable>
        )}
      </div>
    </>
  );

  const connectedUsersSection = (
    <div style={{ ...appDataTableWrapperStyle, borderRadius: "16px", overflow: "hidden" }}>
      {mobile ? (
        <div className="flex flex-column gap-3">
          {visibleActorLinks.length === 0 ? (
            <div style={{ ...appMutedTextStyle, padding: "0.25rem 0.1rem" }}>No relationship requests yet.</div>
          ) : visibleActorLinks.map((link: any) => {
            const status = String(link.status || "").toLowerCase();
            const isIncoming = link.direction === "incoming";
            const displayName = isIncoming
              ? (link.metadata?.requesterDisplayName || "Connected user")
              : (link.metadata?.targetDisplayName || "Connected user");
            const displayEmail = isIncoming
              ? (link.metadata?.requesterEmail || "")
              : (link.metadata?.targetEmail || "");
            const counterpartRoleType = String(isIncoming ? link.requesterRoleType : link.targetRoleType || "").toLowerCase();

            return renderMobileUserCard(
              displayName,
              `${ROLE_LABELS[counterpartRoleType] || counterpartRoleType || "User"}${displayEmail ? ` - ${displayEmail}` : ""}`,
              <>
                {status === "pending" && isIncoming ? (
                  <>
                    <Button type="button" label="Approve" severity="success" rounded size="small" onClick={() => onUpdateActorLink(link, "approved")} />
                    <Button type="button" label="Reject" severity="danger" outlined rounded size="small" onClick={() => onUpdateActorLink(link, "rejected")} />
                  </>
                ) : null}
                {currentRoleType === ROLE_TYPE_PROVIDER && status === "approved" ? (
                  <Button
                    type="button"
                    label="Share Resident"
                    severity="warning"
                    rounded
                    size="small"
                    onClick={() => onPrepareResidentShare({
                      actorLinkId: link.linkId,
                      counterpartId: isIncoming ? link.requesterActorId : link.targetActorId,
                      counterpartRoleType,
                      displayName,
                      displayEmail,
                    })}
                  />
                ) : null}
                {status !== "deleted" ? (
                  <Button type="button" label="Remove" severity="secondary" outlined rounded size="small" onClick={() => onUpdateActorLink(link, "deleted")} />
                ) : null}
              </>,
              <StatusChip label={link.status} />,
            );
          })}
        </div>
      ) : (
        <DataTable value={visibleActorLinks} size="small" loading={loading} emptyMessage="No relationship requests yet.">
          <Column
            header="Connected User"
            body={(link: any) => {
              const isIncoming = link.direction === "incoming";
              const displayName = isIncoming
                ? (link.metadata?.requesterDisplayName || "Connected user")
                : (link.metadata?.targetDisplayName || "Connected user");
              const displayEmail = isIncoming
                ? (link.metadata?.requesterEmail || "")
                : (link.metadata?.targetEmail || "");
              const counterpartRoleType = String(isIncoming ? link.requesterRoleType : link.targetRoleType || "").toLowerCase();

              return (
                <div>
                  <div style={{ fontWeight: 700, color: "#0f172a" }}>{displayName}</div>
                  <div style={{ ...appMutedTextStyle, fontSize: "0.88rem" }}>
                    {ROLE_LABELS[counterpartRoleType] || counterpartRoleType || "User"}
                    {displayEmail ? ` - ${displayEmail}` : ""}
                  </div>
                </div>
              );
            }}
          />
          <Column header="Status" body={(rowData: any) => <StatusChip label={rowData.status} />} style={{ width: "8rem" }} />
          <Column
            header="Actions"
            body={(link: any) => {
              const status = String(link.status || "").toLowerCase();
              const isIncoming = link.direction === "incoming";
              const displayName = isIncoming
                ? (link.metadata?.requesterDisplayName || "Connected user")
                : (link.metadata?.targetDisplayName || "Connected user");
              const displayEmail = isIncoming
                ? (link.metadata?.requesterEmail || "")
                : (link.metadata?.targetEmail || "");
              const counterpartRoleType = String(isIncoming ? link.requesterRoleType : link.targetRoleType || "").toLowerCase();

              return (
                <div className="flex flex-wrap gap-2">
                  {status === "pending" && isIncoming ? (
                    <>
                      <Button type="button" label="Approve" severity="success" rounded size="small" onClick={() => onUpdateActorLink(link, "approved")} />
                      <Button type="button" label="Reject" severity="danger" outlined rounded size="small" onClick={() => onUpdateActorLink(link, "rejected")} />
                    </>
                  ) : null}
                  {currentRoleType === ROLE_TYPE_PROVIDER && status === "approved" ? (
                    <Button
                      type="button"
                      label="Share Resident"
                      severity="warning"
                      rounded
                      size="small"
                      onClick={() => onPrepareResidentShare({
                        actorLinkId: link.linkId,
                        counterpartId: isIncoming ? link.requesterActorId : link.targetActorId,
                        counterpartRoleType,
                        displayName,
                        displayEmail,
                      })}
                    />
                  ) : null}
                  {status !== "deleted" ? (
                    <Button type="button" label="Remove" severity="secondary" outlined rounded size="small" onClick={() => onUpdateActorLink(link, "deleted")} />
                  ) : null}
                </div>
              );
            }}
            style={{ width: "13rem" }}
          />
        </DataTable>
      )}
    </div>
  );

  const sharedResidentsSection = (
    <>
      {currentRoleType === ROLE_TYPE_PROVIDER ? (
        <div style={{ ...appFormSectionStyle, marginBottom: "1rem" }}>
          <div style={{ ...appAccentPanelStyle, marginBottom: "1rem" }}>
            <div style={{ fontWeight: 700, color: "#9a3412" }}>Share resident access in one step</div>
            <div className="mt-2" style={appMutedTextStyle}>
              Choose a resident, then search by the other actor's full email address or pick someone from connected users. {authExperience.brandName} will create the actor link behind the scenes when needed.
            </div>
          </div>
          <div className="grid">
            <div className="col-12 md:col-5">
              <label className="mb-2 block">Resident</label>
              <Dropdown
                options={providerResidentOptions.map((resident) => ({ label: resident.residentName, value: resident.residentId }))}
                value={selectedResidentId}
                onChange={(e) => setSelectedResidentId(e.value)}
                placeholder="Choose a resident"
                style={{ width: "100%" }}
              />
            </div>
            <div className="col-12 md:col-5">
              <label className="mb-2 block">Share with</label>
              {selectedShareTarget ? (
                <div style={{ border: "1px solid #e2e8f0", borderRadius: "14px", padding: "0.85rem", background: "#ffffff" }}>
                  <div style={{ fontWeight: 700, color: "#0f172a" }}>{selectedShareTarget.displayName}</div>
                  <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.88rem" }}>
                    {ROLE_LABELS[selectedShareTarget.counterpartRoleType] || selectedShareTarget.counterpartRoleType}
                    {selectedShareTarget.displayEmail ? ` - ${selectedShareTarget.displayEmail}` : ""}
                  </div>
                  <div className="mt-2">
                    <StatusChip label={selectedShareTarget.actorLinkId ? "connected user" : "direct share"} />
                  </div>
                  <div className="mt-3">
                    <Button type="button" label="Choose Someone Else" severity="secondary" outlined rounded size="small" onClick={() => setSelectedShareTarget(null)} />
                  </div>
                </div>
              ) : (
                <div className="flex flex-column gap-3">
                  <div className="grid">
                    <div className="col-12 md:col-4">
                      <Dropdown
                        options={searchRoleOptions}
                        value={searchRoleType}
                        onChange={(e) => setSearchRoleType(e.value)}
                        placeholder="Role"
                        style={{ width: "100%" }}
                      />
                    </div>
                    <div className="col-12 md:col-8">
                      <div className="flex gap-2">
                        <InputText
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder="Enter full email address"
                          style={{ width: "100%" }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              void onSearch();
                            }
                          }}
                        />
                        <Button
                          type="button"
                          label="Search"
                          severity="secondary"
                          outlined
                          rounded
                          onClick={() => { void onSearch(); }}
                          loading={searching}
                          style={{ flexShrink: 0, minWidth: "7rem" }}
                        />
                      </div>
                    </div>
                  </div>

                  {searchResults.length > 0 ? (
                    <div style={{ border: "1px solid #e2e8f0", borderRadius: "14px", background: "#ffffff", overflow: "hidden" }}>
                      {searchResults.slice(0, 5).map((user: any, index: number) => (
                        <div
                          key={user.userId}
                          style={{
                            padding: "0.85rem",
                            borderBottom: index === Math.min(searchResults.length, 5) - 1 ? "none" : "1px solid #e2e8f0",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: "0.75rem",
                          }}
                        >
                          <div>
                            <div style={{ fontWeight: 700, color: "#0f172a" }}>
                              {`${user.firstName || ""} ${user.lastName || ""}`.trim() || user.email || user.userId}
                            </div>
                            <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.88rem" }}>
                              {ROLE_LABELS[user.userRoleType] || user.userRoleType}
                              {user.email ? ` - ${user.email}` : ""}
                            </div>
                          </div>
                          <Button
                            type="button"
                            label="Share Resident"
                            severity="warning"
                            rounded
                            outlined
                            size="small"
                            onClick={() => onPrepareResidentShare({
                              counterpartId: user.userId,
                              counterpartRoleType: String(user.userRoleType || "").toLowerCase(),
                              displayName: `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.email || user.userId,
                              displayEmail: user.email || "",
                            })}
                          />
                        </div>
                      ))}
                    </div>
                  ) : null}

                  {searchError ? <div className="p-error">{searchError}</div> : null}

                  {approvedActorLinkOptions.length > 0 ? (
                    <>
                      <div style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>Or choose from connected users</div>
                      <Dropdown
                        options={approvedActorLinkOptions}
                        optionLabel="label"
                        optionValue="value"
                        value={selectedApprovedActorLinkId}
                        onChange={(e) => {
                          setSelectedApprovedActorLinkId(e.value);
                          const selectedOption = approvedActorLinkOptions.find((option) => option.value === e.value);
                          setSelectedApprovedActorId(selectedOption?.counterpartId || "");
                        }}
                        placeholder="Choose a connected user"
                        style={{ width: "100%" }}
                      />
                    </>
                  ) : null}
                </div>
              )}
            </div>
            {selectedShareTarget ? (
              <div className="col-12 md:col-2 flex align-items-end">
                <Button type="button" label="Send Share Request" severity="warning" rounded onClick={onCreateResidentRoleLink} />
              </div>
            ) : null}
          </div>
          {residentRequestError ? <div className="p-error mt-2">{residentRequestError}</div> : null}
        </div>
      ) : null}

      <div style={{ ...appDataTableWrapperStyle, borderRadius: "16px", overflow: "hidden" }}>
        {mobile ? (
          <div className="flex flex-column gap-3">
            {visibleResidentRoleLinks.length === 0 ? (
              <div style={{ ...appMutedTextStyle, padding: "0.25rem 0.1rem" }}>No resident access requests yet.</div>
            ) : visibleResidentRoleLinks.map((link: any) => {
              const status = String(link.status || "").toLowerCase();
              const isIncoming = link.direction === "incoming";
              return renderMobileUserCard(
                link.metadata?.providerResidentName || "Resident",
                `${link.metadata?.linkedActorDisplayName || "Connected user"}${link.metadata?.linkedActorEmail ? ` - ${link.metadata.linkedActorEmail}` : ""}`,
                <>
                  {status === "pending" && isIncoming ? (
                    <>
                      <Button type="button" label="Approve" severity="success" rounded size="small" onClick={() => onUpdateResidentRoleLink(link, "approved")} />
                      <Button type="button" label="Reject" severity="danger" outlined rounded size="small" onClick={() => onUpdateResidentRoleLink(link, "rejected")} />
                    </>
                  ) : null}
                  {status !== "deleted" ? (
                    <Button type="button" label="Remove" severity="secondary" outlined rounded size="small" onClick={() => onUpdateResidentRoleLink(link, "deleted")} />
                  ) : null}
                </>,
                <div className="flex align-items-center justify-content-between gap-2">
                  <span style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
                    {ROLE_LABELS[String(link.roleType || "").toLowerCase()] || link.roleType}
                  </span>
                  <StatusChip label={link.status} />
                </div>,
              );
            })}
          </div>
        ) : (
          <DataTable value={visibleResidentRoleLinks} size="small" loading={loading} emptyMessage="No resident access requests yet.">
            <Column
              header="Resident"
              body={(link: any) => (
                <div>
                  <div style={{ fontWeight: 700, color: "#0f172a" }}>{link.metadata?.providerResidentName || "Resident"}</div>
                  <div style={{ ...appMutedTextStyle, fontSize: "0.88rem" }}>Resident access request</div>
                </div>
              )}
            />
            <Column
              header="Connected User"
              body={(link: any) => (
                <div>
                  <div style={{ fontWeight: 700, color: "#0f172a" }}>{link.metadata?.linkedActorDisplayName || "Connected user"}</div>
                  <div style={{ ...appMutedTextStyle, fontSize: "0.88rem" }}>
                    {ROLE_LABELS[String(link.roleType || "").toLowerCase()] || link.roleType}
                    {link.metadata?.linkedActorEmail ? ` - ${link.metadata.linkedActorEmail}` : ""}
                  </div>
                </div>
              )}
            />
            <Column header="Status" body={(rowData: any) => <StatusChip label={rowData.status} />} style={{ width: "8rem" }} />
            <Column
              header="Actions"
              body={(link: any) => {
                const status = String(link.status || "").toLowerCase();
                const isIncoming = link.direction === "incoming";

                return (
                  <div className="flex flex-wrap gap-2">
                    {status === "pending" && isIncoming ? (
                      <>
                        <Button type="button" label="Approve" severity="success" rounded size="small" onClick={() => onUpdateResidentRoleLink(link, "approved")} />
                        <Button type="button" label="Reject" severity="danger" outlined rounded size="small" onClick={() => onUpdateResidentRoleLink(link, "rejected")} />
                      </>
                    ) : null}
                    {status !== "deleted" ? (
                      <Button type="button" label="Remove" severity="secondary" outlined rounded size="small" onClick={() => onUpdateResidentRoleLink(link, "deleted")} />
                    ) : null}
                  </div>
                );
              }}
              style={{ width: "13rem" }}
            />
          </DataTable>
        )}
      </div>
    </>
  );

  return (
    <div style={{ padding: mobile ? "0.85rem" : "1.25rem" }}>
      <Toast ref={toast} />
      <div style={dashboardPageContainerStyle}>
        {renderDashboardHeader(
          "Connections",
          `Find the people you work with on ${authExperience.brandName}, from facility admins and caregivers to registered nurses and mental health professionals. Connect with them to share resident information, coordinate care, and stay updated on important notifications.`,
          <div style={countPillStyle}>{notifications.filter((item: any) => !item.isRead).length} new</div>,
        )}

        <div className="grid align-items-start">
          <div className="col-12 xl:col-8">
            <div style={sectionCardStyle}>
              {mobile ? (
                <div className="flex flex-column gap-3">
                  <div style={mobileSectionShellStyle}>
                    <div className="flex align-items-center justify-content-between gap-3 mb-3">
                      <div style={{ fontWeight: 700, color: "#0f172a" }}>Find {authExperience.brandName} Users</div>
                    </div>
                    {findUsersSection}
                  </div>

                  <div style={mobileSectionShellStyle}>
                    <div className="flex align-items-center justify-content-between gap-3 mb-3">
                      <div style={{ fontWeight: 700, color: "#0f172a" }}>Connected Users</div>
                      <div className="flex align-items-center gap-2">
                        <span style={countPillStyle}>{visibleActorLinks.length}</span>
                        {pendingIncomingActorLinksCount > 0 ? <span style={notificationDotStyle} /> : null}
                      </div>
                    </div>
                    {connectedUsersSection}
                  </div>

                  <div style={mobileSectionShellStyle}>
                    <div className="flex align-items-center justify-content-between gap-3 mb-3">
                      <div style={{ fontWeight: 700, color: "#0f172a" }}>Shared Residents</div>
                      <div className="flex align-items-center gap-2">
                        <span style={countPillStyle}>{visibleResidentRoleLinks.length}</span>
                        {pendingIncomingResidentRoleLinksCount > 0 ? <span style={notificationDotStyle} /> : null}
                      </div>
                    </div>
                    {sharedResidentsSection}
                  </div>
                </div>
              ) : (
                <TabView>
                  <TabPanel header={<TabHeader label={`Find ${authExperience.brandName} Users`} />}>
                    {findUsersSection}
                  </TabPanel>

                  <TabPanel
                    header={
                      <TabHeader
                        label="Connected Users"
                        count={visibleActorLinks.length}
                        showDot={pendingIncomingActorLinksCount > 0}
                      />
                    }
                  >
                    {connectedUsersSection}
                  </TabPanel>

                  <TabPanel
                    header={
                      <TabHeader
                        label="Shared Residents"
                        count={visibleResidentRoleLinks.length}
                        showDot={pendingIncomingResidentRoleLinksCount > 0}
                      />
                    }
                  >
                    {sharedResidentsSection}
                  </TabPanel>
                </TabView>
              )}
            </div>
          </div>

          <div className="col-12 xl:col-4">
            <div style={sectionCardStyle}>
              <div className="flex justify-content-between align-items-center gap-3 mb-3">
                <div>
                  <div style={{ fontWeight: 700, color: "#0f172a" }}>Notifications</div>
                  <div style={{ ...appMutedTextStyle, fontSize: "0.88rem" }}>Recent relationship updates</div>
                </div>
                <span style={countPillStyle}>{notifications.filter((item: any) => !item.isRead).length}</span>
              </div>

              <div className="flex flex-column gap-3" style={notificationsScrollerStyle}>
                {notifications.length === 0 ? (
                  <div style={{ ...appMutedTextStyle, padding: "0.5rem 0" }}>No notifications yet.</div>
                ) : notifications.map((item: any) => (
                  <div key={item.notificationId} style={{ border: "1px solid #e2e8f0", borderRadius: "14px", padding: "0.9rem", background: item.isRead ? "#ffffff" : "#fffaf5" }}>
                    <div className="flex justify-content-between align-items-start gap-3">
                      <div>
                        <div style={{ fontWeight: item.isRead ? 600 : 700, color: "#0f172a" }}>{item.title}</div>
                        <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.88rem", lineHeight: 1.5 }}>{item.message}</div>
                      </div>
                      <StatusChip label={item.isRead ? "read" : "unread"} />
                    </div>
                    {!item.isRead ? (
                      <div className="mt-3">
                        <Button type="button" label="Mark Read" severity="secondary" outlined rounded size="small" onClick={() => onMarkNotificationRead(item.notificationId)} />
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RelationshipCenterPage;
